export type FormatType =
    | 'text'
    | 'number'
    | 'email'
    | 'phone'
    | 'date'
    | 'currency'
    | 'govTaxId'
    | 'fileUrl'
    | 'socialId'
    | 'taxDocument';

export interface ValidationResult {
    isValid: boolean;
    message?: string;
}

export const validateField = (value: string, format: FormatType, fieldLabel: string, maxSize?: number): ValidationResult => {
    if (!value) return { isValid: true };

    const tooLong = maxSize && value.length > maxSize;
    const isNumber = !isNaN(Number(value));

    const validators: Record<FormatType, () => ValidationResult> = {
        text: () => {
            if (tooLong) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ter no máximo ${maxSize} caracteres.` };
            }
            return { isValid: true };
        },
        number: () => {
            if (!isNumber) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser um número.` };
            }
            if (tooLong) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ter no máximo ${maxSize} caracteres.` };
            }
            return { isValid: true };
        },
        email: () => {
            // Suporta múltiplos emails separados por vírgula ou ponto e vírgula
            const emails = value.split(/[,;]/).map(e => e.trim()).filter(e => e.length > 0);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            
            const invalidEmails = emails.filter(email => !emailRegex.test(email));
            
            if (invalidEmails.length > 0) {
                return { 
                    isValid: false, 
                    message: `Coluna "${fieldLabel}" contém email(s) inválido(s): ${invalidEmails.join(', ')}` 
                };
            }
            
            return { isValid: true };
        },
        phone: () => {
            if (!/^(?:\(\d{2}\)|\d{2})[ -]?\d{4,5}[ -]?\d{4}$/.test(value)) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser um telefone válido.` };
            }
            return { isValid: true };
        },
        date: () => {
            if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser uma data válida no formato DD/MM/AAAA.` };
            }
            return { isValid: true };
        },
        currency: () => {
            if (!/^R\$ ?(?:\d{1,3}(?:\.\d{3})*|\d+),\d{2}$/.test(value)) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser um valor monetário válido.` };
            }
            return { isValid: true };
        },
        govTaxId: () => {
            const cleaned = value.replace(/[.\-/\s]/g, '').toUpperCase();
            const isFullCnpj = /^[A-Z0-9]{12}[0-9]{2}$/.test(cleaned) && cleaned.length === 14;
            const isMaskedCnpj = /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-[0-9]{2}$/i.test(value);
            const isRootCnpj = /^[A-Z0-9]{8}$/i.test(cleaned) && cleaned.length === 8;
            const isMaskedRoot = /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}$/i.test(value);
            if (!isFullCnpj && !isMaskedCnpj && !isRootCnpj && !isMaskedRoot) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser um CNPJ válido (14 caracteres) ou raiz do CNPJ (8 caracteres).` };
            }
            return { isValid: true };
        },
        fileUrl: () => {
            if (!/^https?:\/\/[^\s]+$/.test(value)) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser uma URL válida.` };
            }
            return { isValid: true };
        },
        socialId: () => {
            if (!/^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value) && !/^\d{11}$/.test(value)) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser um CPF válido.` };
            }
            return { isValid: true };
        },
        taxDocument: () => {
            const cleaned = value.replace(/[.\-/\s]/g, '').toUpperCase();
            const isCPF = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value) || (/^\d{11}$/.test(cleaned));
            const isCNPJ = /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-[0-9]{2}$/i.test(value)
                || (/^[A-Z0-9]{12}[0-9]{2}$/.test(cleaned) && cleaned.length === 14)
                || (/^[A-Z0-9]{8}$/.test(cleaned) && cleaned.length === 8)
                || /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}$/i.test(value);
            
            if (!isCPF && !isCNPJ) {
                return { isValid: false, message: `Coluna "${fieldLabel}" deve ser um CPF (11 dígitos) ou CNPJ (14 caracteres ou 8 raiz) válido.` };
            }
            return { isValid: true };
        },
    };

    return validators[format]();
};
